import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, ArrowRight, Phone, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const About = () => {
  const { t } = useLang();
  return (
    <>
      <SEO title={t.seo.about.title} description={t.seo.about.description} keywords={t.seo.about.keywords} path="/about" />

      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-cream overflow-hidden">
        <div className="absolute -top-10 right-0 w-72 h-72 bg-[#FFD500] rounded-full blur-[100px] opacity-30 pointer-events-none" />
        <div className="absolute top-20 right-0 w-44 h-44 checkered-fine opacity-20" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 anim-fade-up">
          <div className="bt-tag mb-5">Brothers Taxi</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-zinc-900 max-w-4xl">
            <span className="font-serif italic font-normal normal-case text-zinc-600 block mb-2">{t.accents.fromHeart}</span>
            {t.about.title}
          </h1>
          <p className="mt-6 text-zinc-600 text-xl max-w-3xl">{t.about.subtitle}</p>
        </div>
      </section>

      <section className="bg-paper py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 space-y-8">
          {t.about.content.map((p, i) => (
            <p key={i} className={`text-zinc-700 leading-relaxed ${i === 0 ? "text-xl md:text-2xl font-medium" : "text-lg"}`}>{p}</p>
          ))}
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 rounded-3xl p-10 shadow-taxi-card hover:shadow-taxi-hover hover:-translate-y-1 transition-all">
            <div className="h-14 w-14 rounded-2xl bg-[#FFD500] flex items-center justify-center mb-5">
              <Target className="h-7 w-7 text-zinc-900" strokeWidth={2.2} />
            </div>
            <h2 className="font-display text-3xl font-black uppercase text-zinc-900 mb-4 tracking-tight">{t.about.missionTitle}</h2>
            <p className="text-zinc-700 text-lg leading-relaxed">{t.about.mission}</p>
          </div>
          <div className="bg-zinc-900 text-white rounded-3xl p-10 shadow-taxi-card hover:-translate-y-1 transition-all">
            <div className="h-14 w-14 rounded-2xl bg-[#FFD500] flex items-center justify-center mb-5">
              <Eye className="h-7 w-7 text-zinc-900" strokeWidth={2.2} />
            </div>
            <h2 className="font-display text-3xl font-black uppercase text-white mb-4 tracking-tight">{t.about.visionTitle}</h2>
            <p className="text-zinc-300 text-lg leading-relaxed">{t.about.vision}</p>
          </div>
        </div>
      </section>

      <section className="yellow-noise py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-6 text-zinc-900">
          {[
            { num: "10+", label: t.stats.years },
            { num: "25k+", label: t.stats.trips },
            { num: "20+", label: t.stats.cities },
            { num: "24/7", label: t.stats.support },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-display text-5xl md:text-6xl font-black tracking-tighter">{s.num}</div>
              <div className="text-xs uppercase tracking-widest font-extrabold mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h3 className="font-display text-3xl md:text-5xl font-black uppercase text-zinc-900 mb-6 tracking-tighter">{t.home.ctaBlockTitle}</h3>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book" className="bt-btn-primary">{t.common.bookTaxi} <ArrowRight className="h-4 w-4" /></Link>
            <a href="tel:+355682222286" className="bt-btn-outline"><Phone className="h-4 w-4" /> +355 68 22 22 286</a>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
